<template>
	<div>
		<Header title="Testing">{{ strings.testing.title }}</Header>

		<v-card class="mx-auto">
			<v-list v-for="room of rooms" :key="room.guid">
				<v-list-item dense>
					<v-list-item-title class="subtitle-2" v-text="room.name" />
				</v-list-item>

				<template v-for="device of room.children">
					<v-list-item :key="`${device.guid}-head`" @click="runTests(device)">
						<v-list-item-icon>
							<device-icon
								:device="device"
								:highlight="highlight(device)"
								:error="deviceHasError(device)"
							/>
						</v-list-item-icon>

						<v-list-item-content>
							<v-list-item-title v-text="device.name" />
						</v-list-item-content>

						<v-list-item-action v-if="!testsLoaded || hasTests(device)">
							<v-btn :loading="!testsLoaded || testing(device.guid)" icon>
								<v-icon color="grey lighten-1">fa-play-circle</v-icon>
							</v-btn>
						</v-list-item-action>
					</v-list-item>

					<template v-if="showTests(device)">
						<v-stepper
							:key="`${device.guid}-stepper`"
							v-model="testIdx"
							:elevation="0"
							vertical
						>
							<template v-for="(test, i) in tests(device.guid)">
								<v-stepper-step
									:key="`${device.guid}-${i}`"
									:step="i + 1"
									:complete="test.success"
									:rules="[() => !test.error || test.error]"
								>
									<v-row>
										<v-col cols="8" sm="9">
											{{ test.text }}
										</v-col>
										<v-col
											v-if="test.type === 'manual' && test.testing"
											cols="4"
											sm="3"
											align="center"
										>
											<v-btn
												small
												text
												color="error"
												class="mr-3 ml-3"
												@click="manualTestReject('Manual verification failed')"
												>Fail</v-btn
											>
											<v-btn
												small
												outlined
												color="primary"
												@click="manualTestResolve()"
												>Pass</v-btn
											>
										</v-col>
									</v-row>
								</v-stepper-step>
								<v-stepper-content
									:key="`${device.guid}-${i}-content`"
									:step="i + 1"
								>
									<v-alert :value="!!test.error" color="rust-red" text>
										<span class="body-2">{{ test.error }}</span>
									</v-alert>
								</v-stepper-content>
							</template>
						</v-stepper>
					</template>
				</template>
			</v-list>
		</v-card>

		<NextFab :value="showNext" :next="nextFn">
			<template #tooltip>Testing complete!</template>
		</NextFab>
	</div>
</template>

<style lang="scss" scoped>
.v-list-item .v-list-item__icon {
	height: 40px;
}
</style>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";

import strings from "@/utils/strings";

import Header from "../shared/Header";
import NextFab from "../shared/NextFab";

export default {
	components: { Header, NextFab },

	props: ["nextFn"],

	data: () => ({
		strings,

		testsLoaded: null,
		testingDevice: null,
		testIdx: null,

		manualTestResolve: () => {},
		manualTestReject: () => {},
	}),

	computed: {
		...mapGetters({
			rooms: "devices/rooms",

			tests: "install/tests",
			tested: "install/tested",
			hasError: "install/hasError",
			testing: "install/testing",
			passing: "install/allTestsPass",
		}),

		showNext() {
			return this.testsLoaded && this.passing;
		},
	},

	async mounted() {
		this.testsLoaded = false;
		try {
			await this.loadTests();
		} finally {
			this.testsLoaded = true;
		}
	},

	methods: {
		...mapMutations({
			setTestProp: "install/setTestProp",
		}),
		...mapActions({
			loadTests: "install/loadTests",
		}),

		highlight(device) {
			return this.testsLoaded ? this.tested(device.guid) : false;
		},

		deviceHasError(device) {
			return this.testsLoaded ? this.hasError(device.guid) : false;
		},

		hasTests(device) {
			return this.tests(device.guid).length;
		},

		showTests(device) {
			return (
				this.testingDevice &&
				this.testingDevice.guid === device.guid &&
				this.hasTests(device)
			);
		},

		setTestTesting(item, idx, testing) {
			this.setTestProp({
				guid: item.guid,
				idx,
				key: "testing",
				val: testing,
			});
		},

		setTestSuccess(item, idx, success) {
			this.setTestProp({
				guid: item.guid,
				idx,
				key: "success",
				val: success,
			});
		},

		setTestError(item, idx, error) {
			this.setTestProp({
				guid: item.guid,
				idx,
				key: "error",
				val: error,
			});
		},

		async runTests(device) {
			this.testingDevice = device;
			this.testIdx = 1;

			const tests = this.tests(device.guid);
			for (let i = 0; i < tests.length; i++) {
				const test = tests[i];

				this.setTestError(device, i, null);
				this.setTestTesting(device, i, true);

				// Run the test
				try {
					this.$log(
						`Running test for '${test.device.name}' (${test.device.path})`
					);

					if (test.type === "manual") {
						await new Promise((resolve, reject) => {
							this.manualTestResolve = resolve;
							this.manualTestReject = reject;
						});
					} else {
						await test.run();
					}

					// Mark as success
					this.setTestSuccess(device, i, true);

					// Go to next test in stepper
					this.testIdx++;
				} catch (e) {
					this.setTestSuccess(device, i, false);

					const msg = typeof e === "string" ? e : e.message;
					this.setTestError(device, i, msg);

					this.$log(
						`Error running test for '${test.device.name}' (${test.device.path}): ${e}`
					);

					// Stop here. Don't perform next test.
					break;
				} finally {
					this.manualTestResolve = () => {};
					this.manualTestReject = () => {};
					this.setTestTesting(device, i, false);
				}
			}
		},
	},
};
</script>
